import { useEffect, useMemo, useRef } from "react";

import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { format } from "date-fns";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

import { misc } from "app/themes/colors";
import { useGetStaffBusyTimesQuery } from "services/users";

import { computeFreeSlots, toPercent } from "../availability-timeline.util";
import {
  selectConflicts,
  totalOverlapMinutes
} from "./provider-conflict-modal.util";

type ProviderConflictModalProps = {
  staffMemberId: number;
  staffMemberName: string;
  clinicId?: number;
  clinicTimezone: string;
  // Attempted booking window, clinic-local (same convention as the form's dates).
  attemptedStart: Date;
  attemptedEnd: Date;
  excludeAppointmentId?: number | string;
  onGoBack: () => void;
  onBookAnyway: () => void;
  onSlotPick: (start: Date, end: Date) => void;
  // Pre-form usage (cell-click pre-check) continues INTO the form rather than saving.
  bookAnywayLabel?: string;
  // Pre-check probe: the calendar positions cross-clinic events at their own
  // clinic's wall time, so the client-side intersect hint can be a wall-clock
  // illusion. When the fetch proves no REAL (instant) conflict exists, callers
  // that pass this proceed silently instead of showing a dead-end state.
  onNoConflicts?: () => void;
};

// Mount this component only while it should be visible — the busy-times query runs on
// mount and is dropped on unmount (keepUnusedDataFor: 0), so a re-open always refetches.
export default function ProviderConflictModal({
  staffMemberId,
  staffMemberName,
  clinicId,
  clinicTimezone,
  attemptedStart,
  attemptedEnd,
  excludeAppointmentId,
  onGoBack,
  onBookAnyway,
  onSlotPick,
  bookAnywayLabel = "Book anyway",
  onNoConflicts
}: ProviderConflictModalProps) {
  const dayStamp = format(attemptedStart, "yyyy-MM-dd");
  const { data: rawBusy = [], isFetching } = useGetStaffBusyTimesQuery({
    staffId: staffMemberId,
    afterMoment: zonedTimeToUtc(
      `${dayStamp}T00:00:00`,
      clinicTimezone
    ).toISOString(),
    beforeMoment: zonedTimeToUtc(
      `${dayStamp}T23:59:59`,
      clinicTimezone
    ).toISOString(),
    clinicId,
    excludeAppointmentId
  });

  // All rows rendered in the BOOKING clinic's timezone — rows from other clinics are
  // identified by their chip, never by their own zone, so the lanes stay comparable.
  const rows = useMemo(
    () =>
      rawBusy.map((row) => ({
        ...row,
        start: utcToZonedTime(new Date(row.start), clinicTimezone),
        end: utcToZonedTime(new Date(row.end), clinicTimezone)
      })),
    [rawBusy, clinicTimezone]
  );

  const conflicts = useMemo(
    () => selectConflicts(rows, attemptedStart, attemptedEnd),
    [rows, attemptedStart, attemptedEnd]
  );

  const overlapMinutes = useMemo(
    () => totalOverlapMinutes(rows, attemptedStart, attemptedEnd),
    [rows, attemptedStart, attemptedEnd]
  );

  const durationMinutes = Math.round(
    (attemptedEnd.getTime() - attemptedStart.getTime()) / 60000
  );

  const { displayStart, displayEnd, displayMinutes } = useMemo(() => {
    const endHourOf = (to: Date) =>
      to.getHours() + (to.getMinutes() > 0 ? 1 : 0);
    // A tight window around the collision, not the whole workday — a full-day
    // scale renders a 30-minute conflict as an unreadable sliver. One padding
    // hour each side, clamped to the day.
    let startHour = attemptedStart.getHours();
    let endHour = endHourOf(attemptedEnd);
    rows.forEach(({ start, end }) => {
      startHour = Math.min(startHour, start.getHours());
      endHour = Math.max(endHour, endHourOf(end));
    });
    startHour = Math.max(0, startHour - 1);
    endHour = Math.min(24, endHour + 1);
    const start = new Date(attemptedStart);
    start.setHours(startHour, 0, 0, 0);
    const end = new Date(attemptedStart);
    end.setHours(endHour, 0, 0, 0);
    return {
      displayStart: start,
      displayEnd: end,
      displayMinutes: (endHour - startHour) * 60
    };
  }, [rows, attemptedStart, attemptedEnd]);

  const freeSlots = useMemo(
    () => computeFreeSlots(rows, displayStart, displayEnd, durationMinutes),
    [rows, displayStart, displayEnd, durationMinutes]
  );

  const segment = (start: Date, end: Date) => ({
    left: `${toPercent(start, displayStart, displayMinutes)}%`,
    right: `${100 - toPercent(end, displayStart, displayMinutes)}%`
  });

  const timeRange = (start: Date, end: Date) =>
    `${format(start, "h:mm a")} – ${format(end, "h:mm a")}`;

  const scheduleJustChanged = !isFetching && conflicts.length === 0;

  // The calendar paints cross-clinic events at their HOME clinic's wall time, so
  // a slot can look occupied by events that are hours away in real terms. These
  // are the rows whose home-wall-clock interval overlaps the clicked slot's
  // wall clock — the ones the user is looking at right now.
  const wallShiftedRows = useMemo(() => {
    if (conflicts.length > 0) return [];
    // Compute from the RAW payload — `rows` has start/end already shifted into
    // the booking clinic's wall clock, and re-converting a shifted Date lands on
    // the wrong wall entirely.
    return rawBusy
      .filter((row) => {
        if (!row.clinic_timezone || row.clinic_timezone === clinicTimezone) {
          return false;
        }
        const homeStart = utcToZonedTime(
          new Date(row.start),
          row.clinic_timezone
        );
        const homeEnd = utcToZonedTime(new Date(row.end), row.clinic_timezone);
        return homeStart < attemptedEnd && homeEnd > attemptedStart;
      })
      .map((row) => ({
        ...row,
        // Displayed at their TRUE position on the booking clinic's clock.
        start: utcToZonedTime(new Date(row.start), clinicTimezone),
        end: utcToZonedTime(new Date(row.end), clinicTimezone)
      }));
  }, [rawBusy, conflicts.length, clinicTimezone, attemptedStart, attemptedEnd]);

  const showTimeShiftedInfo =
    Boolean(onNoConflicts) && scheduleJustChanged && wallShiftedRows.length > 0;

  const noConflictsFiredRef = useRef(false);
  useEffect(() => {
    if (
      scheduleJustChanged &&
      wallShiftedRows.length === 0 &&
      onNoConflicts &&
      !noConflictsFiredRef.current
    ) {
      noConflictsFiredRef.current = true;
      onNoConflicts();
    }
  }, [scheduleJustChanged, wallShiftedRows.length, onNoConflicts]);

  // Probe mode: stay invisible until the fetch proves either a real conflict or
  // time-shifted events worth explaining — a modal that flashes open and
  // auto-closes reads as a glitch.
  if (
    onNoConflicts &&
    (isFetching || (conflicts.length === 0 && wallShiftedRows.length === 0))
  ) {
    return null;
  }

  if (showTimeShiftedInfo) {
    return (
      <Dialog
        open
        onClose={onGoBack}
        maxWidth="sm"
        fullWidth
        aria-labelledby="provider-conflict-modal-title"
      >
        <DialogTitle id="provider-conflict-modal-title">
          <Stack direction="row" spacing={1} alignItems="center">
            <WarningAmberIcon color="warning" />
            <span>Different time zone</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Stack spacing={2}>
            <Typography data-testid="provider-conflict-time-shifted">
              <strong>{staffMemberName}</strong> is free at{" "}
              {timeRange(attemptedStart, attemptedEnd)}. The events shown in
              this slot belong to clinics in other time zones — their actual
              times here are:
            </Typography>
            <Stack spacing={1}>
              {wallShiftedRows.map((row, index) => (
                <Stack
                  key={index}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    border: 1,
                    borderColor: "grey.200",
                    borderLeft: 4,
                    borderLeftColor: "grey.400",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75
                  }}
                >
                  <Box>
                    <Typography component="span" fontWeight={700}>
                      {row.type_name || "Busy"}
                    </Typography>
                    <Chip label={row.clinic_name} size="small" sx={{ ml: 1 }} />
                  </Box>
                  <Typography variant="body2" whiteSpace="nowrap">
                    {timeRange(row.start, row.end)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={onGoBack}
            data-testid="provider-conflict-go-back"
          >
            Go back
          </Button>
          <Button
            variant="contained"
            autoFocus
            onClick={onNoConflicts}
            data-testid="provider-conflict-book-free-time"
          >
            Book this time
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onGoBack}
      maxWidth="sm"
      fullWidth
      aria-labelledby="provider-conflict-modal-title"
    >
      <DialogTitle id="provider-conflict-modal-title">
        <Stack direction="row" spacing={1} alignItems="center">
          <WarningAmberIcon color="warning" />
          <span>Provider already booked</span>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pb: 3 }}>
        {isFetching ? (
          <Stack spacing={1.5} data-testid="provider-conflict-skeleton">
            <Skeleton variant="text" width="70%" />
            <Skeleton variant="rounded" height={34} />
            <Skeleton variant="rounded" height={56} />
          </Stack>
        ) : scheduleJustChanged ? (
          <Typography data-testid="provider-conflict-stale">
            This provider&apos;s schedule just changed — no overlapping events
            were found.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Typography>
              <strong>{staffMemberName}</strong> already has{" "}
              {conflicts.length === 1
                ? "an event"
                : `${conflicts.length} events`}{" "}
              during this time.
            </Typography>

            <Box>
              <Box sx={{ position: "relative", height: 14 }}>
                <Typography variant="caption" color="text.disabled">
                  {format(displayStart, "h a")}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ position: "absolute", right: 0 }}
                >
                  {format(displayEnd, "h a")}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "relative",
                  height: 22,
                  bgcolor: "grey.100",
                  borderRadius: 0.75,
                  mb: 0.5
                }}
              >
                {rows.map((row, index) => (
                  <Box
                    key={index}
                    title={`${row.type_name || "Busy"}: ${timeRange(row.start, row.end)}`}
                    sx={{
                      position: "absolute",
                      top: 3,
                      bottom: 3,
                      borderRadius: 0.5,
                      bgcolor: "warning.light",
                      ...segment(row.start, row.end)
                    }}
                  />
                ))}
              </Box>
              <Box
                sx={{
                  position: "relative",
                  height: 22,
                  bgcolor: "grey.100",
                  borderRadius: 0.75
                }}
              >
                <Box
                  data-testid="provider-conflict-your-booking"
                  title={`Your booking: ${timeRange(attemptedStart, attemptedEnd)}`}
                  sx={{
                    position: "absolute",
                    top: 3,
                    bottom: 3,
                    borderRadius: 0.5,
                    border: "1.5px dashed",
                    borderColor: "primary.main",
                    bgcolor: "primary.light",
                    opacity: 0.7,
                    ...segment(attemptedStart, attemptedEnd)
                  }}
                />
              </Box>
              <Stack direction="row" spacing={1.5} sx={{ mt: 0.75 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box
                    sx={{
                      width: 10,
                      height: 4,
                      bgcolor: "warning.light",
                      borderRadius: 0.25
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 10 }}
                  >
                    Already booked
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box
                    sx={{
                      width: 10,
                      height: 4,
                      border: "1.5px dashed",
                      borderColor: "primary.main",
                      borderRadius: 0.25
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 10 }}
                  >
                    Your booking
                  </Typography>
                </Stack>
              </Stack>
            </Box>

            <Stack spacing={1}>
              {conflicts.map((conflict, index) => (
                <Stack
                  key={index}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    border: 1,
                    borderColor: "grey.200",
                    borderLeft: 4,
                    borderLeftColor: "warning.main",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.75
                  }}
                >
                  <Box>
                    <Typography component="span" fontWeight={700}>
                      {conflict.type_name || "Busy"}
                    </Typography>
                    <Chip
                      label={conflict.clinic_name}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                    {conflict.patient_name && (
                      <Typography variant="body2" color="text.secondary">
                        {conflict.patient_name}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" whiteSpace="nowrap">
                    {timeRange(conflict.start, conflict.end)}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              justifyContent="center"
              sx={{
                bgcolor: misc.lightYellow,
                border: 1,
                borderColor: misc.darkYellow,
                borderRadius: 1,
                py: 0.75
              }}
            >
              <WarningAmberIcon color="warning" sx={{ fontSize: 18 }} />
              <Typography variant="body2" color="text.primary">
                {overlapMinutes} min of overlap with this booking
              </Typography>
            </Stack>

            {freeSlots.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography variant="body2" color="text.secondary">
                  Move to a free time:
                </Typography>
                {freeSlots.map((slot, index) => (
                  <Chip
                    key={index}
                    label={timeRange(
                      slot.start,
                      new Date(slot.start.getTime() + durationMinutes * 60000)
                    )}
                    size="small"
                    variant="outlined"
                    color="success"
                    onClick={() =>
                      onSlotPick(
                        slot.start,
                        new Date(slot.start.getTime() + durationMinutes * 60000)
                      )
                    }
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!isFetching && !scheduleJustChanged && (
          <Button
            variant="outlined"
            color="warning"
            onClick={onBookAnyway}
            data-testid="provider-conflict-book-anyway"
          >
            {bookAnywayLabel}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onGoBack}
          autoFocus
          data-testid="provider-conflict-go-back"
        >
          Go back
        </Button>
      </DialogActions>
    </Dialog>
  );
}
